import { useState } from 'react'
import { urlForImage } from 'lib/sanity.image'
import { formatVideoTime } from 'lib/helpers'
import { motion, AnimatePresence } from 'motion/react'
import Image from 'next/image'
import Vimeo from '@u-wave/react-vimeo'

import { PlayPauseIcon } from 'components/Icons/PlayPauseIcon'

export const Video = ({
  layout = 'inset',
  title,
  url,
  poster,
  world = false,
  playsInline = true,
  onDurationChange,
  hidePlayer = false,
  className,
}: {
  layout?: 'inset' | 'full'
  title: string
  url: string
  poster?: any
  world?: boolean
  playsInline?: boolean
  onDurationChange?: (duration: number) => void
  hidePlayer?: boolean
  className?: string
}) => {
  const [duration, setDuration] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [player, setPlayer] = useState<any>(null)
  const [playing, setPlaying] = useState(false)

  const updateDuration = (duration) => {
    setDuration(duration)
    onDurationChange && onDurationChange(duration)
  }
  return (
    <div
      className={`aspect-video h-full w-auto py-10 ${
        !world && layout === 'inset' ? 'px-20' : ''
      }`}
    >
      <div
        className={`relative aspect-video w-full overflow-hidden ${
          layout === 'inset' ? 'rounded-6' : ''
        } leading-[0] ${className}`}
      >
        <AnimatePresence>
          {poster && !loaded ? (
            <motion.div
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0 z-[2] h-full w-full bg-white"
            >
              <Image
                src={urlForImage(poster).width(1000).auto('format').url()}
                alt={title}
                fill
                placeholder="blur"
                blurDataURL={poster?.lqip}
                style={{ objectFit: 'cover' }}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>
        <Vimeo
          className="pointer-events-none will-change-auto"
          responsive
          video={url}
          loop
          playsInline={playsInline}
          controls={false}
          showByline={false}
          showPortrait={false}
          showTitle={false}
          onLoaded={(props) => {
            setLoaded(true)
          }}
          onTimeUpdate={(data) => {
            setCurrentTime(data.seconds)
          }}
          onReady={(player) => {
            player.getDuration().then((data) => {
              updateDuration(data)
            })
            setPlayer(player)
          }}
        />
        {!hidePlayer ? (
          <div
            className={`absolute w-[250px] overflow-hidden rounded-6 bg-white text-8 leading-100 text-gray shadow-md md:w-[320px] md:text-10 md:text-midGray ${
              world
                ? 'bottom-14 left-1/2 -translate-x-1/2 md:bottom-30'
                : ' bottom-14 left-1/2 -translate-x-1/2 md:bottom-20 md:left-20 md:translate-x-[0]'
            }`}
          >
            <div className="relative z-[2] flex select-none items-center justify-between pr-8">
              <div
                role="progressbar"
                aria-valuemax={duration}
                aria-valuenow={currentTime / duration}
                aria-label="Video progress"
                className="absolute inset-0 z-[1] h-full w-full origin-left scale-x-0 bg-[#D6D6D6]"
                style={{
                  transform: `scaleX(${currentTime / duration})`,
                }}
              />
              <button
                className="absolute inset-0 z-[1] h-full w-full bg-transparent"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect()
                  const x = e.clientX - rect.left
                  const percent = x / rect.width
                  if (player) {
                    player.setCurrentTime(duration * percent)
                    setCurrentTime(duration * percent)
                  }
                }}
              />
              <button
                className="relative z-[2] flex items-center px-8 py-8 pr-14 pr-14 md:py-12"
                onClick={async () => {
                  const paused = await player.getPaused()
                  if (paused) {
                    player.play()
                    setPlaying(true)
                  } else {
                    player.pause()
                    setPlaying(false)
                  }
                }}
              >
                <PlayPauseIcon isPlaying={playing} />
              </button>
              <div className="pointer-events-none relative z-[2] flex-1">
                {title}
              </div>
              <div className="pointer-events-none relative z-[2]">
                {formatVideoTime(currentTime)}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
